import React, {useMemo, useState} from "react";
import {FormHeader} from "../shiftRequest/ShiftRequest.styles";
import {Button, Col, Collapse, DatePicker, Input, Row, Select, Tooltip} from "antd";
import {useFetch} from "../../hooks/useFetch";
import {useStore} from "../../hooks/useStore";
import moment from "moment";
import {Link} from "react-router-dom";
import {Column} from "antd/lib/table";
import {
    CityFilter,
    CompanyFilter,
    ManagerCompanyFilter,
    PoolFilter
} from "../_defaultComponents/FilterComponents";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {DownloadOutlined, SearchOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {b64DecodeToUrl, truncateString} from "../../utils/helpers";
import {CloseIcon, SearchInput} from "../_defaultComponents/FilterComponents/FilterComponentStyles";
import {ConditionalTooltip} from "../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import "../../index.css";
import {SubCompanyOnboardingStatuses, getCreditModeLabel} from "../../constants/constants";
import {debounce} from "lodash";
import {useTranslation} from "react-i18next";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {CreditModeSelect} from "../_defaultComponents/CreditModeSelect";

export const SubcompanyPage = () => {
    const stores = useStore();
    // Yönetici Firma filtresi yalnızca admin (ve superadmin) için görünür.
    const isAdmin = stores.meStore.checkRole("isAdmin");
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [searchCityText, setSearchCityText] = useState("all");
    const [activity, setActivity] = useState("all");
    const [onboardingStatus, setOnboardingStatus] = useState("all");
    const [creditMode, setCreditMode] = useState(undefined);
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const [workTypes, setWorkTypes] = useState("all");
    const [managerCompany, setManagerCompany] = useState("all");
    const [companyId, setCompanyId] = useState(undefined);
    const [poolId, setPoolId] = useState("all");
    const [text, setText] = useState("");
    const [creditMin, setCreditMin] = useState(null);
    const [creditMax, setCreditMax] = useState(null);
    const [isFiltersVisible, setIsFiltersVisible] = useState(false);
    const [cariText, setCariText] = useState("");
    const [cariCode, setCariCode] = useState("");
    const [expCode, setExpCode] = useState("");
    const [expText, setExpText] = useState("");
    const [projectCode, setProjectCode] = useState("");
    const [projectCodeText, setProjectCodeText] = useState("");
    const defaultDateRange = [moment().subtract(30, "days"), moment()];
    const [selectedDateRange, setSelectedDateRange] = useState(defaultDateRange);
    const debounceTimeout = 400;
    const {t} = useTranslation();
    const [subcompanyListLoading, subcompanyList, _, __, refetch] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubcompanyPageList.bind(null, {
            companyId,
            limit: 10,
            page: currentPage,
            activity,
            city: searchCityText.toLocaleLowerCase("tr-TR"),
            onboardingStatus: onboardingStatus !== "all" ? onboardingStatus : undefined,
            creditMode,
            workType: workTypes !== "all" ? workTypes : undefined,
            managerCompany: managerCompany !== "all" ? managerCompany : undefined,
            searchText: searchText && searchText.toLocaleLowerCase("tr-TR"),
            cariCode: cariCode ? cariCode.toLocaleLowerCase("tr-TR") : undefined,
            projectCode: projectCode ? projectCode.toLocaleLowerCase("tr-TR") : undefined,
            expCode: expCode ? expCode.toLocaleLowerCase("tr-TR") : undefined,
            excel: false,
            poolId: poolId !== "all" ? poolId : undefined,
            credit:
                creditMin !== null || creditMax !== null
                    ? {
                          min: Number(creditMin),
                          max: Number(creditMax)
                      }
                    : undefined,
            startDate:
                Array.isArray(selectedDateRange) &&
                selectedDateRange.length === 2 &&
                selectedDateRange[0] &&
                selectedDateRange[1]
                    ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                    : undefined,
            endDate:
                Array.isArray(selectedDateRange) &&
                selectedDateRange.length === 2 &&
                selectedDateRange[0] &&
                selectedDateRange[1]
                    ? moment(selectedDateRange[1]).format("YYYY-MM-DD")
                    : undefined
        }),
        showLoading: true,
        dependencies: [
            companyId,
            currentPage,
            onboardingStatus,
            creditMode,
            workTypes,
            searchCityText,
            searchText,
            cariCode,
            expCode,
            projectCode,
            activity,
            poolId,
            creditMin,
            creditMax,
            selectedDateRange,
            managerCompany
        ]
    });
    const onSearchCreditMin = useMemo(
        value => {
            const loadOptions = value => {
                setCreditMin(value);
            };
            return debounce(loadOptions, debounceTimeout);
        },
        [debounceTimeout]
    );

    const onSearchCreditMax = useMemo(
        value => {
            const loadOptions = value => {
                setCreditMax(value);
            };
            return debounce(loadOptions, debounceTimeout);
        },
        [debounceTimeout]
    );

    const getExcelTemplateData = async () => {
        setLoadingExcelData(true);
        try {
            const response = await stores.companyStore.getSubcompanyPageList({
                companyId,
                limit: 10,
                page: currentPage,
                activity,
                city: searchCityText.toLocaleLowerCase("tr-TR"),
                onboardingStatus: onboardingStatus !== "all" ? onboardingStatus : undefined,
                creditMode,
                workType: workTypes !== "all" ? workTypes : undefined,
                managerCompany: managerCompany !== "all" ? managerCompany : undefined,
                searchText: searchText && searchText.toLocaleLowerCase("tr-TR"),
                cariCode: cariCode ? cariCode.toLocaleLowerCase("tr-TR") : undefined,
                projectCode: projectCode ? projectCode.toLocaleLowerCase("tr-TR") : undefined,
                expCode: expCode ? expCode.toLocaleLowerCase("tr-TR") : undefined,
                poolId,
                excel: true,
                startDate:
                    Array.isArray(selectedDateRange) && selectedDateRange.length == 2
                        ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                        : undefined,
                endDate:
                    Array.isArray(selectedDateRange) && selectedDateRange.length == 2
                        ? moment(selectedDateRange?.[1]).format("YYYY-MM-DD")
                        : undefined
            });
            const base64Data = response?.data?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", "sub-companies.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.log("error", error);
        } finally {
            setLoadingExcelData(false);
        }
    };

    const handleDateChange = value => {
        setCurrentPage(1);
        if (!value || value.length === 0) {
            setSelectedDateRange([]);
        } else {
            setSelectedDateRange(value);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                overflow: "scroll"
            }}
        >
            <FormHeader>Alt Firmalar</FormHeader>
            <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                <Row>
                    <Col span={24} data-testid="subCompanyCard">
                        <Collapse
                            activeKey={isFiltersVisible ? "1" : null}
                            onChange={() => setIsFiltersVisible(!isFiltersVisible)}
                            style={{marginTop: "25px", marginBottom: "25px"}}
                        >
                            <Collapse.Panel header="Filtreler" key="1">
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Alt Firma</h5>
                                        <SearchInput
                                            placeholder="Alt Firma"
                                            value={text}
                                            data-testid="searchInput"
                                            onSearch={value => {
                                                setCurrentPage(1);
                                                setSearchText(value);
                                            }}
                                            onChange={event => {
                                                setText(event.target.value);
                                            }}
                                            enterButton={
                                                <Button
                                                    data-testid="searchInput_searchButton"
                                                    icon={<SearchOutlined />}
                                                />
                                            }
                                            suffix={
                                                text ? (
                                                    <CloseIcon
                                                        data-testid="searchInput_deleteButton"
                                                        onClick={() => {
                                                            setCurrentPage(1);
                                                            setSearchText("");
                                                            setText("");
                                                        }}
                                                    />
                                                ) : (
                                                    <span />
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <CompanyFilter
                                            value={companyId}
                                            onChange={value => {
                                                setCompanyId(value);
                                                setCurrentPage(1);
                                            }}
                                            placeholder={t("filters.addFilter", {ns: "common"})}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Şehir</h5>
                                        <CityFilter
                                            showLabel={false}
                                            value={searchCityText}
                                            onChange={value => {
                                                setCurrentPage(1);
                                                setSearchCityText(value);
                                            }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Çalışma Şekli</h5>
                                        <Select
                                            showSearch
                                            value={workTypes}
                                            data-testid="subCompanyWorkType"
                                            style={{width: "100%"}}
                                            onChange={value => {
                                                setCurrentPage(1);
                                                setWorkTypes(value);
                                            }}
                                        >
                                            <Select.Option
                                                value="all"
                                                data-testid="onboardingStatusSelect"
                                            >
                                                Tümü
                                            </Select.Option>
                                            <Select.Option value="pool" data-testid="poolWorktype">
                                                Havuz
                                            </Select.Option>
                                            <Select.Option
                                                value="dedicated"
                                                data-testid="dedicatedWorktype"
                                            >
                                                Dedike
                                            </Select.Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <PoolFilter
                                            value={poolId}
                                            onChange={value => {
                                                setPoolId(value);
                                            }}
                                        />
                                    </Col>
                                    {isAdmin && (
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <ManagerCompanyFilter
                                                selectMarginBottom="0px"
                                                value={managerCompany}
                                                onChange={value => {
                                                    setCurrentPage(1);
                                                    setManagerCompany(value);
                                                }}
                                            />
                                        </Col>
                                    )}
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Firma Aktifliği</h5>
                                        <Select
                                            showSearch
                                            value={activity}
                                            style={{width: "100%"}}
                                            data-testid="subCompanyActivitySelect"
                                            onChange={value => {
                                                setCurrentPage(1);
                                                setActivity(value);
                                            }}
                                        >
                                            <Select.Option
                                                value="all"
                                                data-testid="subCompanyActivitySelect_option"
                                            >
                                                {t("companyDetail.allSubcompanies", {
                                                    ns: "companyDetail"
                                                })}
                                            </Select.Option>
                                            <Select.Option
                                                value="enabled"
                                                data-testid="subCompanyActivitySelect_option"
                                            >
                                                {t("companyDetail.activeSubcompanies", {
                                                    ns: "companyDetail"
                                                })}
                                            </Select.Option>
                                            <Select.Option
                                                value="disabled"
                                                data-testid="subCompanyActivitySelect_option"
                                            >
                                                {t("companyDetail.inactiveSubcompanies", {
                                                    ns: "companyDetail"
                                                })}
                                            </Select.Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Alt Firma Onay Durumu</h5>
                                        <Select
                                            showSearch
                                            value={onboardingStatus}
                                            data-testid="onboardingStatusSelect"
                                            style={{width: "100%"}}
                                            onChange={value => {
                                                setCurrentPage(1);
                                                setOnboardingStatus(value);
                                            }}
                                        >
                                            <Select.Option
                                                value="all"
                                                data-testid="onboardingStatusSelect"
                                            >
                                                Tümü
                                            </Select.Option>
                                            {SubCompanyOnboardingStatuses.map(status => (
                                                <Select.Option
                                                    value={status.value}
                                                    data-testid="onboardingStatusSelect"
                                                >
                                                    {t(
                                                        `fields.subCompanyOnboardingOptions.${status.translation_key}`,
                                                        {ns: "common"}
                                                    )}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <CreditModeSelect
                                            value={creditMode}
                                            allowClear
                                            placeholder="Tümü"
                                            marginBottom="0px"
                                            onChange={value => {
                                                setCurrentPage(1);
                                                setCreditMode(value);
                                            }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Bakiye Aralığı</h5>
                                        <Input.Group compact>
                                            <Input
                                                style={{width: "50%"}}
                                                placeholder="Min"
                                                type="number"
                                                onChange={e => {
                                                    onSearchCreditMin(e.target.value);
                                                }}
                                            />
                                            <Input
                                                style={{width: "50%"}}
                                                placeholder="Max"
                                                type="number"
                                                onChange={e => {
                                                    onSearchCreditMax(e.target.value);
                                                }}
                                            />
                                        </Input.Group>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Cari Kodu</h5>
                                        <SearchInput
                                            placeholder="Cari Kodu"
                                            value={cariText}
                                            data-testid="cariCodeSearch"
                                            onSearch={value => {
                                                setCurrentPage(1);
                                                setCariCode(value);
                                            }}
                                            onChange={event => {
                                                setCariText(event.target.value);
                                            }}
                                            enterButton={
                                                <Button
                                                    data-testid="cariCodeSearch_searchButton"
                                                    icon={<SearchOutlined />}
                                                />
                                            }
                                            suffix={
                                                cariText ? (
                                                    <CloseIcon
                                                        data-testid="cariCodeSearch_deleteButton"
                                                        onClick={() => {
                                                            setCurrentPage(1);
                                                            setCariCode("");
                                                            setCariText("");
                                                        }}
                                                    />
                                                ) : (
                                                    <span />
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>PN Kodu</h5>
                                        <SearchInput
                                            placeholder="PN Kodu"
                                            value={expText}
                                            data-testid="expCodeSearch"
                                            onSearch={value => {
                                                setCurrentPage(1);
                                                setExpCode(value);
                                            }}
                                            onChange={event => {
                                                setExpText(event.target.value);
                                            }}
                                            enterButton={
                                                <Button
                                                    data-testid="expCodeSearch_searchButton"
                                                    icon={<SearchOutlined />}
                                                />
                                            }
                                            suffix={
                                                expText ? (
                                                    <CloseIcon
                                                        data-testid="expCodeSearch_deleteButton"
                                                        onClick={() => {
                                                            setCurrentPage(1);
                                                            setExpText("");
                                                            setExpCode("");
                                                        }}
                                                    />
                                                ) : (
                                                    <span />
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>Proje Kodu</h5>
                                        <SearchInput
                                            placeholder="Proje Kodu"
                                            value={projectCodeText}
                                            data-testid="projectCodeSearch"
                                            onSearch={value => {
                                                setCurrentPage(1);
                                                setProjectCode(value);
                                            }}
                                            onChange={event => {
                                                setProjectCodeText(event.target.value);
                                            }}
                                            enterButton={
                                                <Button
                                                    data-testid="projectCodeSearch_searchButton"
                                                    icon={<SearchOutlined />}
                                                />
                                            }
                                            suffix={
                                                projectCodeText ? (
                                                    <CloseIcon
                                                        data-testid="projectCodeSearch_deleteButton"
                                                        onClick={() => {
                                                            setCurrentPage(1);
                                                            setProjectCodeText("");
                                                            setProjectCode("");
                                                        }}
                                                    />
                                                ) : (
                                                    <span />
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                        <h5>
                                            Başvuru Tarihi Aralığı{" "}
                                            <Tooltip title="Son 30 günün verisi gösterilmektedir.">
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </h5>
                                        <DatePicker.RangePicker
                                            placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                                            onChange={handleDateChange}
                                            value={
                                                selectedDateRange && selectedDateRange.length === 2
                                                    ? selectedDateRange
                                                    : undefined
                                            }
                                            allowClear
                                        />
                                    </Col>
                                </Row>
                                {stores.meStore.checkPermission("subcompanies.export") && (
                                    <Row>
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <div style={{marginTop: "15px", marginBottom: "15px"}}>
                                                <CustomButton
                                                    type="primary"
                                                    data-testid="exportWithExcelButton"
                                                    onClick={async () => {
                                                        setLoadingExcelData(true);
                                                        await getExcelTemplateData();
                                                    }}
                                                    loading={loadingExcelData}
                                                    icon={<DownloadOutlined />}
                                                >
                                                    Excel Çıktısı İndir
                                                </CustomButton>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </Collapse.Panel>
                        </Collapse>
                    </Col>
                    <Col span={24}>
                        <ResponsiveDataTable
                            rowKey="_id"
                            data-testid="subCompanyTable"
                            dataSource={subcompanyList && subcompanyList?.data?.rows}
                            loading={subcompanyListLoading}
                            scroll={{x: 1500}}
                            style={{overflowX: "auto"}}
                            pagination={{
                                total: subcompanyList?.data?.dataCount,
                                current: currentPage,
                                onChange: async page => {
                                    setCurrentPage(page);
                                },
                                hideOnSinglePage: true,
                                showSizeChanger: false,
                                responsive: true
                            }}
                            locale={{
                                emptyText: <EmptyMessage />
                            }}
                            bordered
                        >
                            <Column
                                title={t("fields.subcompanyName", {ns: "common"})}
                                dataIndex="subCompanyName"
                                width={200}
                                render={(text, index) => (
                                    <ConditionalTooltip
                                        condition={truncateString(text, 16).indexOf("...") > 0}
                                        text={text}
                                        child={
                                            <div
                                                data-testid="subCompanyName"
                                                style={{maxWidth: "200px"}}
                                            >
                                                <Link
                                                    data-testid="subCompanyName"
                                                    target="_blank"
                                                    to={{
                                                        pathname:
                                                            "/companies/sub-company/" + index._id
                                                    }}
                                                >
                                                    {truncateString(text, 16)}
                                                </Link>
                                            </div>
                                        }
                                    />
                                )}
                                onCell={() => ({
                                    "data-label": t("fields.subcompanyName", {ns: "common"})
                                })}
                            />
                            <Column
                                title={t("fields.companyName", {ns: "common"})}
                                dataIndex="companyName"
                                width={150}
                                render={(text, index) => (
                                    <Link
                                        data-testid="companyName"
                                        target="_blank"
                                        to={{
                                            pathname: "/companies/" + index.companyId
                                        }}
                                    >
                                        {text}
                                    </Link>
                                )}
                                onCell={() => ({
                                    "data-label": t("fields.companyName", {ns: "common"})
                                })}
                            />
                            <Column
                                title="Alt Firma Onay Durumu"
                                dataIndex="onboardingStatus"
                                width={150}
                                render={status => (
                                    <span>
                                        {status && status !== "undefined"
                                            ? t(`fields.subCompanyOnboardingOptions.${status}`, {
                                                  ns: "common"
                                              })
                                            : "-"}
                                    </span>
                                )}
                                onCell={() => ({
                                    "data-label": "Proje Kodu"
                                })}
                            />
                            <Column
                                title="Kredi Modu"
                                dataIndex="creditMode"
                                width={150}
                                render={creditMode => (
                                    <span>{creditMode ? getCreditModeLabel(creditMode) : "-"}</span>
                                )}
                                onCell={() => ({
                                    "data-label": "Kredi Modu"
                                })}
                            />
                            <Column
                                title="Cari Kodu"
                                dataIndex="cariCode"
                                width={150}
                                render={cariCode => <span>{cariCode ? cariCode : "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Cari Kodu"
                                })}
                            />
                            <Column
                                title="Proje Kodu"
                                dataIndex="projectCode"
                                width={150}
                                render={projectCode => (
                                    <span>{projectCode ? projectCode : "-"}</span>
                                )}
                                onCell={() => ({
                                    "data-label": "Proje Kodu"
                                })}
                            />
                            <Column
                                title="PN Kodu"
                                dataIndex="expCode"
                                width={150}
                                render={expCode => <span>{expCode ? expCode : "-"}</span>}
                                onCell={() => ({
                                    "data-label": "PN Kodu"
                                })}
                            />
                            {/* <Column
                                title="Netsis Bakiye"
                                dataIndex="netsisCredit"
                                width={150}
                                render={netsisCredit => (
                                    <span>{netsisCredit ? netsisCredit + " TL" : "-"}</span>
                                )}
                                onCell={() => ({
                                    "data-label": "Netsis Bakiye"
                                })}
                            /> */}
                            <Column
                                title="Vergi Numarası"
                                dataIndex="taxNumber"
                                width={150}
                                render={taxNumber => <span>{taxNumber ? taxNumber : "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Vergi Numarası"
                                })}
                            />
                            <Column
                                title="Vergi Dairesi"
                                dataIndex="registeredTaxOffice"
                                width={150}
                                render={registeredTaxOffice => (
                                    <span>{registeredTaxOffice ? registeredTaxOffice : "-"}</span>
                                )}
                                onCell={() => ({
                                    "data-label": "Vergi Dairesi"
                                })}
                            />
                            <Column
                                title="Bakiye"
                                dataIndex="credit"
                                width={150}
                                render={credit => (
                                    <span>
                                        {credit &&
                                            (credit !== 9999999 ? credit : "Kredi ile çalışmıyor")}
                                    </span>
                                )}
                                onCell={() => ({
                                    "data-label": "Bakiye"
                                })}
                            />
                            <Column
                                title="Çalışma Şekli"
                                width={150}
                                dataIndex="subCompanyWorkType"
                                render={status => (
                                    <span>{status === "dedicated" ? "Dedike" : "Havuz"}</span>
                                )}
                                onCell={() => ({
                                    "data-label": "Çalışma Şekli"
                                })}
                            />
                            <Column
                                title="Havuz Bölgesi"
                                width={150}
                                dataIndex="poolName"
                                render={region => <span>{region ? region : "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Havuz Bölgesi"
                                })}
                            />
                            <Column
                                title={t("fields.city", {ns: "common"})}
                                width={150}
                                dataIndex="city"
                                render={text => (
                                    <span data-testid="city">{text?.capitalize()}</span>
                                )}
                                onCell={() => ({
                                    "data-label": t("fields.city", {ns: "common"})
                                })}
                            />
                            <Column
                                title={t("fields.county", {ns: "common"})}
                                width={150}
                                dataIndex="county"
                                render={text => (
                                    <span data-testid="county">{text?.capitalize()}</span>
                                )}
                                onCell={() => ({
                                    "data-label": t("fields.county", {ns: "common"})
                                })}
                            />
                            <Column
                                title={t("companyDetail.fields.district", {ns: "companyDetail"})}
                                width={200}
                                dataIndex="district"
                                render={text => (
                                    <span data-testid="district">{text?.capitalize()}</span>
                                )}
                                onCell={() => ({
                                    "data-label": t("companyDetail.fields.district", {
                                        ns: "companyDetail"
                                    })
                                })}
                            />
                            <Column
                                title="Başvuran Mail Adresi"
                                width={200}
                                dataIndex="authorizedEmail"
                                render={authorizedEmail => <span>{authorizedEmail || "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Başvuran Mail Adresi"
                                })}
                            />
                            <Column
                                title="Başvuran Kişi"
                                width={150}
                                dataIndex="authorizedPerson"
                                render={authorizedPerson => <span>{authorizedPerson || "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Başvuran Kişi"
                                })}
                            />
                            <Column
                                title="Başvuran Telefon"
                                width={150}
                                dataIndex="authorizedPhone"
                                render={authorizedPhone => <span>{authorizedPhone || "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Başvuran Telefon"
                                })}
                            />
                            <Column
                                title="Başvuru Tarihi"
                                width={150}
                                dataIndex="createdAt"
                                render={createdAt => (
                                    <span>
                                        {createdAt
                                            ? moment(createdAt).format("DD.MM.YYYY HH:mm:ss")
                                            : "-"}
                                    </span>
                                )}
                                onCell={() => ({
                                    "data-label": "Başvuru Tarihi"
                                })}
                            />
                            <Column
                                title="Referans ismi"
                                width={150}
                                dataIndex={["reference", "fullName"]}
                                render={text => <span>{text || "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Referans ismi"
                                })}
                            />
                            <Column
                                title="Referans Telefonu"
                                width={150}
                                dataIndex={["reference", "phone"]}
                                render={text => <span>{text || "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Referans Telefonu"
                                })}
                            />
                            <Column
                                title="Referans Kullanıcı Adı"
                                width={150}
                                dataIndex={["reference", "username"]}
                                render={text => <span>{text || "-"}</span>}
                                onCell={() => ({
                                    "data-label": "Referans Kullanıcı Adı"
                                })}
                            />
                        </ResponsiveDataTable>
                    </Col>
                </Row>
            </div>
        </div>
    );
};
